import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Upload, FileText, Download, Trash2, Eye, Search, ArrowLeft, Info } from 'lucide-react';
import '../../../styles/np-theme.css';

interface UploadedFile {
    id: string;
    name: string;
    size: number;
    type: string;
    uploadDate: Date;
    category: string;
    url: string;
}

interface KnowledgeUploadProps {
    title?: string;
    description?: string;
    defaultCategory?: string;
    onBack?: () => void;
    icon?: React.ReactNode;
    color?: string;
}

const CATEGORIES = ['Manuals', 'Reports', 'Diagrams', 'Procedures', 'Training', 'Contracts', 'Warranties', 'Other'];

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const KnowledgeUpload: React.FC<KnowledgeUploadProps> = ({
    title = "Knowledge Base",
    description = "Upload and manage technical documents, manuals, and resources",
    defaultCategory = "all",
    onBack,
    icon,
}) => {
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [dragActive, setDragActive] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    // Category values are compared case-insensitively; 'all' shows every file.
    const [selectedCategory, setSelectedCategory] = useState<string>(defaultCategory.toLowerCase());
    const inputRef = useRef<HTMLInputElement>(null);
    const filesRef = useRef<UploadedFile[]>([]);
    filesRef.current = files;

    // Release object URLs when leaving the page
    useEffect(() => () => filesRef.current.forEach(f => URL.revokeObjectURL(f.url)), []);

    // New files take the category of the section they were uploaded from, so they show up there.
    const uploadCategory = defaultCategory.toLowerCase() === 'all' ? 'Other' : defaultCategory;

    const handleFiles = useCallback((fileList: FileList) => {
        const newFiles: UploadedFile[] = Array.from(fileList).map((file, index) => ({
            id: `${Date.now()}-${index}`,
            name: file.name,
            size: file.size,
            type: file.type,
            uploadDate: new Date(),
            category: uploadCategory,
            url: URL.createObjectURL(file),
        }));
        setFiles(prev => [...prev, ...newFiles]);
    }, [uploadCategory]);

    const handleDrag = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFiles(e.dataTransfer.files);
        }
    }, [handleFiles]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            handleFiles(e.target.files);
        }
        e.target.value = '';
    };

    const deleteFile = (id: string) => {
        setFiles(prev => {
            const gone = prev.find(f => f.id === id);
            if (gone) URL.revokeObjectURL(gone.url);
            return prev.filter(file => file.id !== id);
        });
    };

    const filteredFiles = files.filter(file => {
        const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || file.category.toLowerCase() === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="np-page min-h-screen">
            <div className="np-theme max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                {/* Page header */}
                <div className="flex flex-wrap items-center gap-3 pb-5 border-b border-slate-200">
                    {onBack && (
                        <button
                            type="button"
                            onClick={onBack}
                            aria-label="Back to all document categories"
                            className="inline-flex items-center justify-center w-9 h-9 rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                        >
                            <ArrowLeft size={17} />
                        </button>
                    )}
                    <div className="np-pagehead">
                        <div className="np-pagehead__mark">
                            {icon || <FileText size={20} />}
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <h1 className="np-pagehead__title">{title}</h1>
                            <div className="np-pagehead__meta">
                                <span className="np-pagehead__desc">{description}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="np-note mt-5" role="status">
                    <Info size={15} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <p className="text-sm">
                        Document storage is not connected to the server yet. Files you add here stay in this browser tab
                        only and are cleared when you leave or reload the page.
                    </p>
                </div>

                {/* Upload area */}
                <section className="mt-5 bg-white border border-slate-200 rounded p-5" aria-labelledby="doc-upload-title">
                    <h2 id="doc-upload-title" className="mb-3">Add documents</h2>
                    <div
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        className={`rounded border-2 border-dashed px-6 py-10 text-center transition-colors ${dragActive
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-300 bg-slate-50 hover:border-blue-300'
                            }`}
                    >
                        <input
                            ref={inputRef}
                            type="file"
                            id="file-upload"
                            multiple
                            onChange={handleChange}
                            className="sr-only"
                            tabIndex={-1}
                            aria-hidden="true"
                        />
                        <span className="inline-flex items-center justify-center w-12 h-12 rounded bg-blue-100 text-blue-600">
                            <Upload size={22} aria-hidden="true" />
                        </span>
                        <p className="mt-3 text-sm text-slate-600">
                            Drag and drop files here, or{' '}
                            <button
                                type="button"
                                onClick={() => inputRef.current?.click()}
                                className="text-blue-700 underline underline-offset-2 hover:text-blue-800"
                            >
                                choose files
                            </button>
                        </p>
                        <p className="mt-1 text-xs text-slate-500">PDF, DWG, DOCX or any other file, up to 50 MB each</p>
                    </div>
                </section>

                {/* Search and filter */}
                {files.length > 0 && (
                    <div className="mt-5 flex flex-col md:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                            <input
                                type="text"
                                placeholder="Search documents"
                                aria-label="Search documents"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full h-9 pl-9 pr-3 rounded border border-slate-300 bg-white text-sm outline-none focus:ring-2 focus:ring-blue-200"
                            />
                        </div>
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            aria-label="Filter by category"
                            className="h-9 px-3 rounded border border-slate-300 bg-white text-sm outline-none focus:ring-2 focus:ring-blue-200"
                        >
                            <option value="all">All categories</option>
                            {CATEGORIES.map(cat => (
                                <option key={cat} value={cat.toLowerCase()}>{cat}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Files */}
                {files.length > 0 && (
                    <section className="mt-3 bg-white border border-slate-200 rounded overflow-hidden" aria-labelledby="doc-list-title">
                        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                            <h2 id="doc-list-title" className="text-sm">Documents ({filteredFiles.length})</h2>
                        </div>
                        {filteredFiles.length === 0 ? (
                            <p className="px-4 py-10 text-center text-sm text-slate-500">No documents match your search or category.</p>
                        ) : (
                            <ul className="divide-y divide-slate-100">
                                {filteredFiles.map((file) => (
                                    <li key={file.id} className="flex items-center justify-between gap-4 px-4 py-3">
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <span className="inline-flex items-center justify-center w-9 h-9 rounded bg-blue-50 text-blue-600 flex-shrink-0">
                                                <FileText size={17} aria-hidden="true" />
                                            </span>
                                            <div className="min-w-0">
                                                <p className="text-sm text-slate-900 truncate">{file.name}</p>
                                                <p className="mt-0.5 text-xs text-slate-500">
                                                    {formatFileSize(file.size)} · {file.uploadDate.toLocaleDateString('en-IN')} · {file.category}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            <a
                                                href={file.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`View ${file.name}`}
                                                title="View"
                                                className="p-2 rounded text-slate-600 hover:bg-slate-100 hover:text-blue-700"
                                            >
                                                <Eye size={17} />
                                            </a>
                                            <a
                                                href={file.url}
                                                download={file.name}
                                                aria-label={`Download ${file.name}`}
                                                title="Download"
                                                className="p-2 rounded text-slate-600 hover:bg-slate-100 hover:text-blue-700"
                                            >
                                                <Download size={17} />
                                            </a>
                                            <button
                                                type="button"
                                                onClick={() => deleteFile(file.id)}
                                                aria-label={`Remove ${file.name}`}
                                                title="Remove"
                                                className="p-2 rounded text-slate-600 hover:bg-red-50 hover:text-red-600"
                                            >
                                                <Trash2 size={17} />
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                )}

                {/* Empty state */}
                {files.length === 0 && (
                    <div className="mt-5 bg-white border border-slate-200 rounded px-6 py-12 text-center">
                        <span className="inline-flex items-center justify-center w-12 h-12 rounded bg-slate-100 text-slate-400">
                            <FileText size={22} aria-hidden="true" />
                        </span>
                        <p className="mt-3 text-sm text-slate-900">No documents in this category yet</p>
                        <p className="mt-1 text-xs text-slate-500">Add a file above to see it listed here.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default KnowledgeUpload;
